/**
 * PromotedRule — a reviewer-authored, locally-promoted canonical rule
 * (RFC-0003 ① / ADR-0010, Accepted).
 *
 * When a reviewer accepts a discovered change *as canonical*, they do NOT mutate
 * the vendored, SHA-verified, frozen seed baseline. Instead they author a curated
 * `PromotedRule` into a separate runtime store (`promoted_rules`). A D2 converter
 * (`promotedRuleToRule`) expands this curated subset into the full vendored `Rule`
 * shape so it flows through `buildCards` unchanged.
 *
 * Trust boundary (ADR-0002/0004/0010 red line): advisory → canonical happens ONLY
 * through human authoring + an official https `officialSourceUrl` + a `sourceSha256`
 * + a reviewer signature. The LLM/discovery pipeline only *drafts* the form; it can
 * never auto-promote. The `LP-` id prefix marks every locally-promoted rule and is
 * how the board derives the "locally promoted · not declared-scope baseline" badge
 * (D2) — a promoted rule is NEVER counted toward the declared-scope coverage claim.
 *
 * Applicability is ADR-0010 model 4a (owner-confirmed): jurisdiction-gated, with an
 * optional single config-field condition. The reviewer never hand-writes DSL.
 */

import { z } from "zod";

import { LegalEffect, ObligationKind, ObligationTiming, RegulatoryDomain } from "./primitives";

/**
 * ADR-0010 model 4a. The rule applies when the config's target markets include
 * one of `jurisdictions`; an optional single config-field equality narrows it
 * further (the converter emits a `declarative` `trigger_logic` from this). Omit
 * the config-field pair for a jurisdiction-only gate.
 */
export const PromotedRuleApplicabilitySchema = z
  .object({
    mode: z.literal("jurisdiction_gated"),
    jurisdictions: z.array(z.string().min(1)).min(1),
    configField: z.string().min(1).optional(),
    // The config menu in D3 is categorical (powertrain/automation enums, boolean
    // flags) — a string|boolean equality covers it. Numeric config gates are
    // deliberately out of scope for 4a; widen here (+ the D2 converter) if added.
    configEquals: z.union([z.string(), z.boolean()]).optional(),
  })
  .superRefine((val, ctx) => {
    // configField + configEquals are an all-or-nothing pair: a named field with no
    // comparison value (or a value with no field) is a malformed condition that the
    // D2 converter must never receive. Enforce the invariant at the contract edge.
    const hasField = val.configField !== undefined;
    const hasEquals = val.configEquals !== undefined;
    if (hasField !== hasEquals) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasField ? "configEquals" : "configField"],
        message: "configField and configEquals must be provided together",
      });
    }
  });
export type PromotedRuleApplicability = z.infer<typeof PromotedRuleApplicabilitySchema>;

export const PromotedRuleSchema = z.object({
  // `LP-` prefix = locally promoted. Distinct from the vendored `REG-*` namespace
  // so D2's declared-scope-exclusion red line can assert no `LP-` id is ever
  // counted complete.
  id: z.string().regex(/^LP-[A-Za-z0-9-]+$/, "id must use the LP- (locally-promoted) prefix"),
  title: z.string().min(1),
  regulatoryDomain: RegulatoryDomain,
  jurisdiction: z.string().min(1),
  obligationKind: ObligationKind,
  obligationTiming: ObligationTiming,
  legalEffect: LegalEffect,
  requirementText: z.string().min(1),
  legalReference: z.object({
    instrument: z.string().min(1),
    article: z.string().min(1).optional(),
  }),
  // The advisory→canonical bar: a reviewer-signed official source + its hash.
  officialSourceUrl: z
    .string()
    .url()
    .refine((u) => u.startsWith("https://"), "officialSourceUrl must be https"),
  sourceSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/, "sourceSha256 must be lowercase 64-hex (normalised form)"),
  applicability: PromotedRuleApplicabilitySchema,
  reviewer: z.string().email(),
  // ISO 8601 UTC timestamp (D3 stamps it server-side via `new Date().toISOString()`).
  // `.datetime()` keeps garbage out of the audit trail.
  promotedAt: z.string().datetime(),
  // The discovered signal this promotion originated from (audit trail back to the
  // candidate that was reviewed). Free-form id — discovery ids are `disc-*`.
  originatingSignalId: z.string().min(1),
});
export type PromotedRule = z.infer<typeof PromotedRuleSchema>;
